/**
 * Markdown 渲染工具(含 JSX)
 *
 * P2-M11 拆分:清洗逻辑已移至 cleanMarkdown.ts(纯字符串处理,无 JSX)
 * 本文件仅保留:内联样式解析 + 完整 markdown 文本到 React 节点的渲染
 */
import React from 'react';
import { stripThinkingAndToolCallTags } from '../tagParser';
// P2-M11: 清洗逻辑(不完整 markdown 结构处理)拆分到独立纯函数模块
export { stripIncompleteMarkdown } from './cleanMarkdown';
import { stripIncompleteMarkdown } from './cleanMarkdown';

export type FontSizeVariant = 'sm' | 'lg';

const FONT_SIZES: Record<FontSizeVariant, {
  inlineCode: string;
  linkPill: string;
  fallback: string;
  table: string;
  h1: string;
  h2: string;
  h3: string;
  h4: string;
  list: string;
  listMarker: string;
  olBadge: string;
  quote: string;
  codeLabel: string;
  code: string;
  paragraph: string;
}> = {
  sm: {
    inlineCode: 'text-[11px]',
    linkPill: 'text-[10px]',
    fallback: 'text-[12px]',
    table: 'text-[11px]',
    h1: 'text-base',
    h2: 'text-sm',
    h3: 'text-[13px]',
    h4: 'text-[12px]',
    list: 'text-[12px]',
    listMarker: 'w-3 h-3 mt-[3px] rounded-full bg-white/[0.06] text-gray-400 text-[8px] text-center leading-[12px]',
    olBadge: 'w-5 h-5 rounded-full bg-white/[0.06] text-gray-300 text-[11px] text-center leading-[20px]',
    quote: 'text-[12px]',
    codeLabel: 'text-[10px]',
    code: 'text-[11px]',
    paragraph: 'text-[12px]',
  },
  lg: {
    inlineCode: 'text-[18px]',
    linkPill: 'text-[14px]',
    fallback: 'text-[18px]',
    table: 'text-[16px]',
    h1: 'text-[26px]',
    h2: 'text-[22px]',
    h3: 'text-[19px]',
    h4: 'text-[17px]',
    list: 'text-[17px]',
    listMarker: 'w-4 h-4 mt-[5px] rounded-full bg-white/[0.06] text-gray-400 text-[11px] text-center leading-[16px]',
    olBadge: 'w-7 h-7 rounded-full bg-white/[0.06] text-gray-300 text-[15px] text-center leading-[28px]',
    quote: 'text-[16px]',
    codeLabel: 'text-[13px]',
    code: 'text-[15px]',
    paragraph: 'text-[17px]',
  },
};

export const parseInlineStyles = (text: string, fontSizeVariant: FontSizeVariant = 'sm'): React.ReactNode[] => {
  // P0(v8): 使用 [^\]]* 和 [^)]* 替代 .*? 非贪婪匹配
  // 原正则 \[.*?\]\(.*?\) 中 .*? 遇到 URL 中的 ) 会提前截断，导致链接不完整
  // 改用字符类排除法：[^\]]* 匹配非 ] 字符，[^)]* 匹配非 ) 字符
  // 🔴 P2-6: 升级链接正则为两层嵌套括号支持
  // 模式: (?:[^()]|\((?:[^()]|\([^()]*\))*\))* 允许 URL 内含 article(1)(2) 或 func(a(b)) 这类结构
  const twoLevelParen = '(?:[^()]|\\((?:[^()]|\\([^()]*\\))*\\))*';
  const linkPattern = `\\[[^\\]]*\\]\\(${twoLevelParen}\\)`;
  const bt = '`';
  // 🔴 P5: 裸 URL 自动链接检测 — 匹配 http(s):// 开头的 URL
  // 模式: https?:// 后接 URL 允许的字符（排除空格、中英文标点作为结尾符）
  // 用 (?:[^\s<>\"'`])+ 匹配 URL 主体，再手动排除末尾的中文标点和括号
  const bareUrlPattern = 'https?:\\/\\/[^\\s<>"\'`)]+(?:\\([^\\s)]*\\)|[^\\s<>"\'`).,;!?])*';
  const splitRegex = new RegExp(`(\\*\\*.*?\\*\\*|${linkPattern}|${bt}[^${bt}]+${bt}|${bareUrlPattern})`, 'g');
  const parts = text.split(splitRegex);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <span key={i} className="text-white font-bold mx-0.5">{part.slice(2, -2)}</span>;
    }
    // P2: 行内代码支持（`code` → 灰色背景等宽字体）
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={i} className={`px-1.5 py-0.5 rounded bg-white/[0.08] ${FONT_SIZES[fontSizeVariant].inlineCode} text-white/90 font-mono mx-0.5`}>
          {part.slice(1, -1)}
        </code>
      );
    }
    // 🔴 P2-6: 同步链接匹配正则为两层嵌套括号支持，与 split 正则保持一致
    const linkMatch = part.match(/^\[([^\]]*)\]\(((?:[^()]|\((?:[^()]|\([^()]*\))*\))*)\)$/);
    if (linkMatch) {
      const [, label, url] = linkMatch;
      // 🔴 P2-2: URL 协议安全验证 — 只允许 http/https/ftp 协议
      // 防止 javascript:、data:、vbscript: 等危险协议在链接中被执行
      const isSafeUrl = /^https?:\/\//i.test(url) || /^ftp:\/\//i.test(url);
      if (!isSafeUrl) {
        // 不安全协议 → 降级为纯文本显示
        return <span key={i}>{label}</span>;
      }
      // 🔴 P2-4: 当链接标签是完整 URL 时，截断为只显示域名
      // AI 偶尔输出 [https://en.wikipedia.org/wiki/VeryLongTitle](URL) 格式
      // 显示完整 URL 作为标签文字太长，提取域名作为显示文本
      let displayLabel = label;
      if (/^https?:\/\//i.test(label) && label.length > 30) {
        try {
          const host = new URL(label).hostname;
          displayLabel = host.replace(/^www\./, '');
        } catch {
          // URL 解析失败，保留原始标签
        }
      }
      // 来源链接样式 — 统一使用灰圆框白字 pill/badge 样式（表格内外一致）
      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center px-1.5 py-0.1 rounded-l-full rounded-r-full ${FONT_SIZES[fontSizeVariant].linkPill} text-white/90 bg-white/[0.1] hover:bg-white/[0.15] border border-white/[0.06] transition-colors align-middle no-underline select-none`}
          onClick={(e) => e.stopPropagation()}
          title={url}
        >
          {displayLabel}
        </a>
      );
    }
    // 🔴 P5: 裸 URL 检测（AI 可能直接输出 https://example.com 而非 [label](URL) 格式）
    // 检测条件: 以 http:// 或 https:// 开头，且不是已被 [label](URL) 匹配的形式
    const bareUrlMatch = part.match(/^(https?:\/\/[^\s<>\"'`)]+(?:\([^\s)]*\)|[^\s<>\"'`).,;!?])*)$/);
    if (bareUrlMatch) {
      const url = bareUrlMatch[1];
      // 从 URL 提取域名作为显示文本（限制长度，过长则截断）
      let displayLabel = url;
      try {
        const host = new URL(url).hostname;
        displayLabel = host.replace(/^www\./, '');
        if (displayLabel.length > 30) {
          displayLabel = displayLabel.slice(0, 28) + '...';
        }
      } catch {
        // URL 解析失败，使用前 30 个字符作为显示文本
        displayLabel = url.length > 30 ? url.slice(0, 28) + '...' : url;
      }
      // 裸 URL 统一使用灰圆框白字 pill 样式
      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center px-1.5 py-0.1 rounded-l-full rounded-r-full ${FONT_SIZES[fontSizeVariant].linkPill} text-white/90 bg-white/[0.1] hover:bg-white/[0.15] border border-white/[0.06] transition-colors align-middle no-underline select-none`}
          onClick={(e) => e.stopPropagation()}
          title={url}
        >
          {displayLabel}
        </a>
      );
    }
    return part;
  });
};

const renderFormattedMessageInner = (text: string, fontSizeVariant: FontSizeVariant = 'sm') => {
  // P1(v8): 外层 try-catch 防止任何渲染异常导致整个组件崩溃
  // 流式场景中文本不完整可能触发各种边界异常，捕获后降级为纯文本
  try {
    return _renderFormattedMessageInnerCore(text, fontSizeVariant);
  } catch {
    // 降级：按换行分段显示纯文本，避免 SummaryErrorBoundary 捕获异常后闪烁
    const fallbackLines = text.split('\n');
    return (
      <div className="space-y-0.5 select-text cursor-text">
        {fallbackLines.map((line, i) => (
          <div key={`fb-${i}`} className={`${FONT_SIZES[fontSizeVariant].fallback} text-gray-300 leading-relaxed`}>
            {line || '\u00A0'}
          </div>
        ))}
      </div>
    );
  }
};

const _renderFormattedMessageInnerCore = (text: string, fontSizeVariant: FontSizeVariant = 'sm') => {
  const fs = FONT_SIZES[fontSizeVariant];
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let inTable = false;
  let tableRows: string[][] = [];
  let tableKey = '';
  // P2: 代码块状态
  let inCodeBlock = false;
  let codeBlockKey = '';
  let codeBlockLang = '';
  let codeBlockLines: string[] = [];

  // 单元格内换行统一处理（2026-10-15）：\n（渲染容错并入的断行）与 <br>（提示词约束模型输出）
  const renderCell = (cell: string) => {
    const segments = cell.replace(/<br\s*\/?>/gi, '\n').split('\n');
    return segments.map((seg, si) => (
      <div key={si} className={si > 0 ? 'mt-1' : ''}>{parseInlineStyles(seg.trim(), fontSizeVariant)}</div>
    ));
  };

  const flushTable = () => {
    if (tableRows.length === 0) return;
    const headerRow = tableRows[0];
    // 列数对齐（2026-10-15）：以表头列为基准，缺列补空、超列并入末列，
    // 避免 AI 输出的行内断行导致单元格错位
    const colCount = headerRow.length;
    const bodyRows = tableRows.slice(1).map((row) => {
      if (row.length === colCount) return row;
      if (row.length < colCount) return [...row, ...Array<string>(colCount - row.length).fill('')];
      const fixed = row.slice(0, colCount - 1);
      fixed.push(row.slice(colCount - 1).join('\n'));
      return fixed;
    });
    elements.push(
      <div key={tableKey} className="my-3 overflow-x-auto rounded-lg border border-white/10 shadow-sm">
        <table className={`w-full ${fs.table}`}>
          <thead>
            <tr className="bg-white/[0.06]">
              {headerRow.map((cell, ci) => (
                <th key={ci} className="px-3 py-2 text-left text-gray-300 font-semibold border-b border-white/10 whitespace-nowrap">
                  {renderCell(cell.trim())}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, ri) => (
              <tr key={ri} className="border-b border-white/5 last:border-0 hover:bg-white/[0.04] transition-colors">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 text-gray-400">
                    {renderCell(cell.trim())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
    inTable = false;
  };

  // P1修复：分隔行必须同时包含 - 和 |，避免 --- 水平线被误判为表格分隔行
  const separatorRegex = /^\|?[\s:]*-[\s\-:|]*\|[\s\-:|]*$/;
  // 🔴 P2-修复：放宽表格起始行检测，支持 GFM 表格常见格式
  // - 标准格式: "| Header | Header |" （原已支持）
  // - 无首尾竖线: "Header | Header | Header" （AI 较常输出）
  // 判定条件: 至少包含 2 个 | 且分割出 ≥2 个非空段，避免普通文本（如"选项A | 选项B"）误判
  const isPotentialTableRow = (text: string): boolean => {
    // 标准格式：| Cell | Cell |
    if (/^\|\s*\S[^|]*\s*\|/.test(text)) return true;
    // 宽松格式：Cell | Cell | Cell（至少2个|，分割出≥2个非空段）
    const pipeCount = (text.match(/\|/g) || []).length;
    if (pipeCount >= 2) {
      const segments = text.split('|').filter(s => s.trim().length > 0);
      if (segments.length >= 2) return true;
    }
    return false;
  };

  // 判断一行是否像表格分隔行（如 "|---|---|" 或 "---:|---:|"）
  const isSeparatorRow = (text: string): boolean => {
    return separatorRegex.test(text.trim());
  };

  // 判断一行是否像表格数据行（进入表格模式后的宽松判定）
  const isTableDataRow = (text: string): boolean => {
    // 标准 Markdown 表格行
    if (/^\|\s*\S[^|]*\|/.test(text)) return true;
    const pipeCount = (text.match(/\|/g) || []).length;
    if (pipeCount >= 2) {
      const segments = text.split('|').filter(s => s.trim().length > 0);
      return segments.length >= 2;
    }
    return false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const key = `line-${i}`;
    const trimmed = line.trim();

    if (inTable && separatorRegex.test(trimmed)) {
      continue;
    }

    // 进入表格模式的判定：首次需要包含至少2个 | 且分割出≥2个非空段
    // 已在表格内时：允许更宽松的判定以兼容 AI 可能的格式变化
    const isTableRow = inTable ? isTableDataRow(trimmed) : isPotentialTableRow(trimmed);
    if (isTableRow && !trimmed.startsWith('#')) {
      // 🔴 P2-修复：放宽后仍需防误判 — 如果首行只有1个管道符且非标准格式，按普通段落处理
      // 注意：isPotentialTableRow 已要求 pipeCount >= 2，这里的防御主要针对 isTableDataRow 的宽松逻辑
      const pipeCount = (trimmed.match(/\|/g) || []).length;
      if (!inTable && pipeCount < 2) {
        // 首行管道符不足2个，按普通段落处理（此分支在 isPotentialTableRow 下实际不会触发，仅作防御）
      } else {
        const cleanCells = trimmed.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
        if (!inTable) {
          inTable = true;
          tableKey = key;
        }
        tableRows.push(cleanCells);
        continue;
      }
    } else if (inTable) {
      // 表格区域内遇到空行时，跳过而非立即刷新表格
      // （AI 输出的表格行间可能存在空行，不应截断表格）
      if (!trimmed) {
        continue;
      }
      // 非表格行容错（2026-10-15）：AI 常在单元格内裸换行分点（如视频脚本
      // 「快切镜头 1/2/3」逐行写），逐行解析会截断表格致渲染错乱。向前看：
      // 若跳过连续非空非表格行后仍回到表格行，则这些行是单元格内断行 →
      // 并入上一行末单元格（\n join，由 renderCell 渲染为换行）；否则是
      // 真正的表格结束 → flush 后按段落渲染。
      let j = i + 1;
      while (
        j < lines.length &&
        lines[j].trim() &&
        !isTableDataRow(lines[j].trim()) &&
        !separatorRegex.test(lines[j].trim())
      ) {
        j++;
      }
      if (j < lines.length && lines[j].trim() && (isTableDataRow(lines[j].trim()) || separatorRegex.test(lines[j].trim()))) {
        if (tableRows.length > 0) {
          const lastRow = tableRows[tableRows.length - 1];
          lastRow[lastRow.length - 1] += '\n' + [trimmed, ...lines.slice(i + 1, j).map((l) => l.trim())].join('\n');
        }
        i = j - 1;
        continue;
      }
      flushTable();
    }

    if (!trimmed) {
      elements.push(<div key={key} className="h-3" />);
      continue;
    }

    if (line.startsWith('# ')) {
      elements.push(
        <h1 key={key} className={`${fs.h1} font-bold text-white mt-5 mb-3 border-b border-white/10 pb-2.5`}>
          {parseInlineStyles(line.replace(/^#\s/, ''), fontSizeVariant)}
        </h1>
      );
      continue;
    }

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={key} className={`${fs.h2} font-bold text-white mt-4 mb-2 flex items-center gap-2`}>
          <span className="w-1.5 h-4 bg-gray-500 rounded-full inline-block" />
          {parseInlineStyles(line.replace(/^##\s/, ''), fontSizeVariant)}
        </h2>
      );
      continue;
    }

    if (line.startsWith('### ')) {
      const content = line.replace(/^###\s/, '');
      elements.push(
        <h3 key={key} className={`${fs.h3} font-semibold text-gray-200 mt-3 mb-1.5 flex items-center gap-1.5`}>
          <span className="text-gray-400">•</span>
          {parseInlineStyles(content, fontSizeVariant)}
        </h3>
      );
      continue;
    }

    if (line.startsWith('#### ')) {
      const content = line.replace(/^####\s/, '');
      elements.push(
        <h4 key={key} className={`${fs.h4} font-semibold text-gray-300 mt-2 mb-1`}>{parseInlineStyles(content, fontSizeVariant)}</h4>
      );
      continue;
    }

    if (line.startsWith('- ')) {
      // 修复复制换行：标记不能是 flex 盒子（Chrome 复制序列化把 flex 子项
      // 当块级，标记与文本间被插入换行）。改用 inline-block + 显式空格，
      // 复制得到 "• 文本" 单行；父级同步去 flex。
      elements.push(
        <div key={key} className={`${fs.list} text-gray-300 leading-relaxed ml-1`}>
          <span className={`inline-block mr-2 align-top ${fs.listMarker}`}>•</span>
          {' '}{parseInlineStyles(line.replace(/^-\s/, ''), fontSizeVariant)}
        </div>
      );
      continue;
    }

    if (/^\d+\.\s/.test(line)) {
      const match = line.match(/^(\d+)\.\s(.*)$/);
      if (match) {
        // 同上：inline-block 序号徽标 + 显式空格，复制得到 "1 文本" 单行
        elements.push(
          <div key={key} className={`${fs.list} text-gray-300 leading-relaxed ml-1`}>
            <span className={`inline-block mr-2 font-bold align-top ${fs.olBadge}`}>
              {match[1]}
            </span>
            {' '}{parseInlineStyles(match[2], fontSizeVariant)}
          </div>
        );
      }
      continue;
    }

    if (line.startsWith('> ')) {
      elements.push(
        <div key={key} className={`pl-4 border-l-2 border-gray-500 bg-white/[0.02] rounded-r-lg py-2 my-2 ${fs.quote} text-gray-400 italic`}>
          {parseInlineStyles(line.replace(/^>\s/, ''), fontSizeVariant)}
        </div>
      );
      continue;
    }

    if (line.startsWith('```')) {
      // P2: 代码块开始/结束标记
      if (!inCodeBlock) {
        inCodeBlock = true;
        codeBlockKey = key;
        codeBlockLang = line.replace(/^```\s*/, '').trim();
        codeBlockLines = [];
      } else {
        // 代码块结束，渲染完整代码块
        inCodeBlock = false;
        elements.push(
          <div key={codeBlockKey} className="my-2 rounded-lg border border-white/10 bg-black/30 overflow-hidden">
            {codeBlockLang && (
              <div className={`px-3 py-1 ${fs.codeLabel} text-gray-500 border-b border-white/5 bg-white/[0.02] font-mono`}>
                {codeBlockLang}
              </div>
            )}
            <pre className={`px-3 py-2 ${fs.code} text-gray-300 font-mono overflow-x-auto leading-relaxed`}>
              {codeBlockLines.join('\n')}
            </pre>
          </div>
        );
      }
      continue;
    }

    // P2: 代码块内容收集
    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }

    elements.push(
      <div key={key} className={`${fs.paragraph} text-gray-300 leading-relaxed`}>{parseInlineStyles(line, fontSizeVariant)}</div>
    );
  }

  if (inTable) {
    flushTable();
  }

  // P2: 处理未闭合的代码块（流式场景中代码块可能尚未结束）
  if (inCodeBlock && codeBlockLines.length > 0) {
    elements.push(
      <div key={codeBlockKey} className="my-2 rounded-lg border border-white/10 bg-black/30 overflow-hidden">
        {codeBlockLang && (
          <div className={`px-3 py-1 ${fs.codeLabel} text-gray-500 border-b border-white/5 bg-white/[0.02] font-mono`}>
            {codeBlockLang}
          </div>
        )}
        <pre className={`px-3 py-2 ${fs.code} text-gray-300 font-mono overflow-x-auto leading-relaxed`}>
          {codeBlockLines.join('\n')}
        </pre>
      </div>
    );
  }

  return <div className="space-y-0.5 select-text cursor-text">{elements}</div>;
};

/**
 * @param text - 要渲染的文本
 * @param skipIncomplete - 流式场景下=true：对不完整的 markdown 结构（表格、代码块等）
 *                        先转回纯文本，避免视觉闪烁。历史消息传 false 以保留原始格式。
 * @param fontSizeVariant - 字号变体：'sm'（默认，聊天/搜索等窄面板）；'lg'（画布结果节点等大字号场景）
 */
export const renderFormattedMessage = (text: string, skipIncomplete = false, fontSizeVariant: FontSizeVariant = 'sm') => {
  // P6(v6): preserveWhitespace=true 保留换行符，避免 markdown 格式被破坏；
  //          最后显式 .trim() 只清理首尾空白，不影响内部结构。
  let cleanedText = stripThinkingAndToolCallTags(text, true).trim();

  // P6-1: 流式进行中：把不完整表格/代码块转回纯文本，防止"闪烁"
  if (skipIncomplete) {
    cleanedText = stripIncompleteMarkdown(cleanedText);
  }

  return renderFormattedMessageInner(cleanedText, fontSizeVariant);
};
